import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  type CarrierSearchMatch,
  MIN_CARRIER_NAME_LENGTH,
  useCarrierSearch,
} from '../../models/carrierSearch'
import type { ChatActionWithValue } from '../../models/chat'
import { CarrierSearchResults } from './CarrierSearchResults'

interface ChatCarrierNameSearchProps {
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  onEnterDot: () => void
  readonly?: boolean
}

/** "I don't remember my DOT": finds the carrier by company name and submits the DOT they pick. */
export function ChatCarrierNameSearch({ handleSubmit, onEnterDot, readonly }: ChatCarrierNameSearchProps) {
  const [name, setName] = useState('')
  const search = useCarrierSearch()
  const trimmed = name.trim()
  const canSearch = !readonly && trimmed.length >= MIN_CARRIER_NAME_LENGTH

  function onSearch(event: React.FormEvent) {
    event.preventDefault()
    if (!canSearch || search.isPending) return
    search.mutate(trimmed)
  }

  function pick(carrier: CarrierSearchMatch) {
    handleSubmit({
      content: { component: 'P', content: `${carrier.legalName} (DOT ${carrier.dotNumber})` },
      value: carrier.dotNumber,
    })
  }

  return (
    <div className="mt-2 flex w-full flex-col items-end gap-3">
      <form onSubmit={onSearch} className="flex w-full max-w-sm items-center gap-2">
        <Input
          type="text"
          autoComplete="organization"
          autoFocus
          aria-label="Company name"
          placeholder="Company name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={readonly}
        />
        <Button type="submit" disabled={!canSearch} loading={search.isPending ? 'Searching…' : undefined}>
          Search
        </Button>
      </form>

      {search.isError && (
        <p role="alert" className="text-destructive text-right text-sm">
          Company search isn&apos;t working right now. Try again, or enter your DOT number.
        </p>
      )}
      {search.data && (
        <CarrierSearchResults
          carriers={search.data.results}
          hasMore={search.data.hasMore}
          onPick={pick}
          readonly={readonly}
        />
      )}

      <Button type="button" variant="ghost" onClick={onEnterDot} disabled={readonly}>
        Enter my DOT number instead
      </Button>
    </div>
  )
}
